/**
 * @file ShopIntegrationCard.tsx
 * @description Карточка вида интеграции во вкладке «Интеграции» магазина
 * (phase19.28 этап А; uiux-platform-shops §6; PL13): название, политика
 * («только своя» / «общая допустима»), источник настройки пилюлей и
 * действия по реестру `shopIntegrationActions`.
 *
 * Вид, которого кабинет ещё не знает (нет в `INTEGRATION_KIND_META`), —
 * только просмотр: формы ключей для него нет.
 */

import { ArrowRightLeft, Pencil, Plus, type LucideIcon } from "lucide-react";

import type { ShopIntegrationItem } from "../../../lib/api-platform-shops";
import {
  ACTION_LABEL,
  isKnownKind,
  POLICY_LABEL,
  scopeMeta,
  shopIntegrationActions,
  type ShopIntegrationAction,
} from "../../../lib/platform/shop-integration-meta";
import { formatPlatformDate } from "../../../lib/platform/shop-format";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { StatusPill } from "../../ui/StatusPill";

const ACTION_ICON: Readonly<Record<ShopIntegrationAction, LucideIcon>> = {
  edit: Pencil,
  own: Plus,
  connect: Plus,
  transfer: ArrowRightLeft,
};

interface Props {
  item: ShopIntegrationItem;
  busy: boolean;
  onAction: (item: ShopIntegrationItem, action: ShopIntegrationAction) => void;
}

export function ShopIntegrationCard({ item, busy, onAction }: Props) {
  const known = isKnownKind(item.kind);
  const actions = shopIntegrationActions(item).filter((action) => known || action === "transfer");
  return (
    <article
      aria-label={item.title}
      className="flex min-w-0 flex-col gap-3 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]"
    >
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="type-card-title min-w-0 break-words">{item.title}</h3>
        <Badge variant="outline">{POLICY_LABEL[item.policy]}</Badge>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill meta={scopeMeta(item)} />
        {item.updatedAt && item.scope !== "NONE" ? (
          <span className="type-caption text-on-surface-variant">изменена {formatPlatformDate(item.updatedAt)}</span>
        ) : null}
      </div>
      {actions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {actions.map((action, index) => {
            const Icon = ACTION_ICON[action];
            return (
              <Button
                key={action}
                variant={index === 0 ? "soft" : "outline"}
                onClick={() => onAction(item, action)}
                disabled={busy}
              >
                <Icon className="size-4" aria-hidden />
                {ACTION_LABEL[action]}
              </Button>
            );
          })}
        </div>
      ) : null}
    </article>
  );
}
